import { Warning24 } from '@carbon/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { TeamsBulkUpdateStatus } from 'api/teams';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { UserTag } from 'components/atoms/select';
import { HelperText01, ProductiveHeading05 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { getUserById } from 'utils/idb-utils';
import { StatusType } from './placeholder-resolution';

const MainHeader = styled.div`
  ${ProductiveHeading05};
`;
const Description = styled.div`
  ${HelperText01};
  color: var(--gray-80);
  margin-top: var(--spacing-02);
`;
const InprogressContainer = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--heading-5-weight);
  color: var(--gray-70);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const IntiatedContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Title = () => {
  return (
    <>
      <MainHeader>
        <FormattedMessage id="projects.placeholder_resolution.modal.title" />
      </MainHeader>
      <Description>
        <FormattedMessage id="projects.placeholder_resolution.modal.title.description" />
      </Description>
    </>
  );
};

type InitiatedByType = {
  initiatedUser: User;
};

const InitiatedBy = (props: InitiatedByType) => {
  const { initiatedUser } = props;

  return (
    <IntiatedContainer>
      <FormattedMessage id="projects.placeholders.update.inprogress.initiatedby" />
      : &nbsp;
      <UserTag
        color={getRandomColor(initiatedUser.userId)}
        imageUrl={initiatedUser.photoUrl}
        label={fullName(initiatedUser)}
        userStatus={initiatedUser.userStatus}
      />
    </IntiatedContainer>
  );
};

type ModalPropsHelper = {
  visible: boolean;
  inProgress: boolean;
  placeholderMetaLoading: boolean;
  onClose: () => void;
  bulkUpdateStatus: string | undefined;
  onSubmit: () => void;
  isLoading: boolean;
  setBulkUpdateStatus: (arg: StatusType) => void;
  teamsUpdateStatusInfo: TeamsBulkUpdateStatus[] | undefined;
};

const useModalPropsHelper = (props: ModalPropsHelper) => {
  const {
    visible,
    inProgress,
    placeholderMetaLoading,
    onClose,
    bulkUpdateStatus,
    onSubmit,
    isLoading,
    setBulkUpdateStatus,
    teamsUpdateStatusInfo,
  } = props;

  const [initiatedUser, setInitiatedUser] = useState<User | null>(null);

  const fetchUser = useCallback(async () => {
    if (!teamsUpdateStatusInfo || !teamsUpdateStatusInfo[0]?.createdBy) {
      return;
    }

    const user = (await getUserById(
      teamsUpdateStatusInfo[0]?.createdBy,
    )) as User;

    setInitiatedUser(user);
  }, [teamsUpdateStatusInfo]);

  useEffect(() => {
    if (
      bulkUpdateStatus === 'IN_PROGRESS' ||
      bulkUpdateStatus === 'SUBMITTED' ||
      bulkUpdateStatus === 'FAILED'
    ) {
      fetchUser();
    }
  }, [teamsUpdateStatusInfo, fetchUser, bulkUpdateStatus]);

  const commonProps = {
    visible,
    width: 600,
    okButtonProps: {
      loading: inProgress,
      disabled: placeholderMetaLoading,
      className: 'project_placeholder-resolution_confirmation',
    },
    onCancel: onClose,
  };

  const failedTitle = useTranslation(
    'projects.placeholders.update.failed.title',
  );

  if (Boolean(bulkUpdateStatus === 'FAILED') && !isLoading && initiatedUser) {
    const ErrorTitle = styled.div`
      font-size: 20px;
      display: flex;
      align-items: center;
    `;

    return {
      ...commonProps,
      ...{
        title: (
          <ErrorTitle>
            <Warning24 /> &nbsp; {failedTitle}
          </ErrorTitle>
        ),
        footer: [
          <InprogressContainer>
            <InitiatedBy initiatedUser={initiatedUser} />
            <div>
              <Button onClick={onClose}>
                <FormattedMessage id="close" />
              </Button>
              <Button
                onClick={() => {
                  setBulkUpdateStatus('NONE');
                }}
                type="primary"
              >
                <FormattedMessage id="projects.preview.menu.manage_placeholder" />
              </Button>
            </div>
          </InprogressContainer>,
        ],
      },
    };
  }

  if (bulkUpdateStatus === 'NONE') {
    return {
      ...commonProps,
      ...{
        onOk: onSubmit,
        title: <Title />,
        onCancel: onClose,
        cancelButtonProps: {
          type: 'text',
          className: 'project_placeholder-resolution_cancel',
        },
        okText: <FormattedMessage id="done" />,
      },
    };
  }

  if (
    Boolean(bulkUpdateStatus === 'IN_PROGRESS') &&
    initiatedUser &&
    !isLoading
  ) {
    return {
      ...commonProps,
      ...{
        title: <Loading size="large" />,
        footer: [
          <InprogressContainer>
            <InitiatedBy initiatedUser={initiatedUser} />
            <Button onClick={onClose}>
              <FormattedMessage id="close" />
            </Button>
          </InprogressContainer>,
        ],
      },
    };
  }

  return commonProps;
};

export { useModalPropsHelper };
